// App.js - Enhanced version with configurable layouts
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Header from './components/Header';
import Home from './pages/Home';
import Services from './pages/Services';
import About from './pages/About';
import Contact from './pages/Contact';
import Footer from './components/Footer';
import MatrixLoader from './components/MetaverseLoader';
import { AnimatePresence } from 'framer-motion';
import './App.css';
import './styles/animations.css';
import Careers from './pages/Careers';
import Training from './pages/Training';
import Changer from './logochanger/Changer';
import QRCodeGenerator from './components/QRCodeGenerator';
import ProductsToolsPage from './pages/ProductsToolsPage';
import ColorPaletteGenerator from './components/ColorPaletteGenerator';
import ImageOptimizer from './components/ImageOptimizer';
import CodeFormatterApp from './components/CodeFormatterApp';
import PartnersServicePage from './pages/PartnersServicePage';
import NotFoundPage from './components/NotFoundPage';
import UiComponents from './components/ui/DocumentationPage';
import APITester from './components/APITester';
import PerformanceAnalyzer from './components/PerformanceAnalyzer';
import ScrollToTop from './components/ScrollTop';
import FloatingParticles from './components/3d/FloatingParticles';
import EmailService from './pages/EmailService';
import StudentApp from './studentapps/StudentApp';
import BusinessQR from './components/BusinessQR';
import MediaScanner from './components/MediaScanner';
import DeveloperConsole from './components/DeveloperConsole';
import SnakeGame from './components/games/SnakeGame';
import SlidingPuzzle from './components/games/SlidingPuzzle';
import MemoryMatch from './components/games/MemoryMatch';
import ColorMatch from './components/games/ColorMatch';
import PuzzleHub from './components/PuzzleHub';
import MathReflex from './components/games/MathReflex';
import SequenceMemory from './components/games/SequenceMemory';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import CollaborateAndPricing from './pages/CollaborateAndPricing';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#ff4d4d',
    },
    secondary: {
      main: '#8b0000',
    },
    background: {
      default: '#0a1128',
      paper: '#0f1a3a',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 800,
    },
    h2: {
      fontWeight: 700,
    },
    h3: {
      fontWeight: 600,
    },
  },
});

// Define which routes should have the main layout
const routesWithMainLayout = [
  // Main pages
  "/",
  "/services",
  "/about",
  "/careers",
  "/training",
  "/contact",
  "/pricing",

  // Products & tools
  "/products-tools",
  "/logo-changer",
  "/qr-code-generator",
  "/color-palette",
  "/image-optimizer",
  "/code-formatter",
  "/business-qr",
  "/media-scanner",

  // Developer tools
  "/ui-components",
  "/api-hub",
  "/performance-analyzer",
  "/developer-console",

  // Services
  "/partners-service",
  "/email-service",

  // Games / hub
  "/puzzle-hub",
  "/snake-game",
  "/sliding-puzzle",
  "/memory-match",
  "/color-match",
  "/math-reflex",
  "/seq-memory",

  // Legal pages (keep footer/header)
  "/privacy-policy",
  "/terms-conditions",
];


// Routes that should have floating particles
const routesWithParticles = [
  '/',
  '/services',
  '/about',
  '/careers',
  '/training',
  '/contact',
  '/products-tools',
  '/logo-changer',
  '/qr-code-generator',
  '/color-palette',
  '/image-optimizer',
  '/code-formatter',
  '/ui-components',
  '/api-hub',
  '/performance-analyzer',
  '/partners-service',
  '/email-service',
  '/business-qr',
  '/media-scanner',
  '/pricing'
];

const AppContent = () => {
  const location = useLocation();

  const shouldShowMainLayout = routesWithMainLayout.includes(location.pathname);
  const shouldShowParticles = routesWithParticles.includes(location.pathname);

  return (
    <div className="App">
      {/* Conditionally render header and footer */}
      {shouldShowMainLayout && <Header />}

      <AnimatePresence mode="wait">
        <ScrollToTop />
        
        {/* Conditionally render particles */}
        {shouldShowParticles && <FloatingParticles count={15} />}

        <Routes location={location} key={location.pathname}>
          {/* Main Pages */}
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/about" element={<About />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/training" element={<Training />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/pricing" element={<CollaborateAndPricing />} />

          {/* Products & Tools */}
          <Route path="/products-tools" element={<ProductsToolsPage />} />
          <Route path="/logo-changer" element={<Changer />} />
          <Route path="/qr-code-generator" element={<QRCodeGenerator />} />
          <Route path="/color-palette" element={<ColorPaletteGenerator />} />
          <Route path="/image-optimizer" element={<ImageOptimizer />} />
          <Route path="/code-formatter" element={<CodeFormatterApp />} />
          <Route path="/business-qr" element={<BusinessQR />} />
          <Route path="/media-scanner" element={<MediaScanner />} />

          {/* Development Tools */}
          <Route path="/ui-components" element={<UiComponents />} />
          <Route path="/api-hub" element={<APITester />} />
          <Route path="/performance-analyzer" element={<PerformanceAnalyzer />} />
          <Route path="/developer-console" element={<DeveloperConsole />} />


          {/* Services */}
          <Route path="/partners-service" element={<PartnersServicePage />} />
          <Route path="/email-service" element={<EmailService />} />

          {/* Student Application - Has its own header/footer */}
          <Route path="/student-app" element={<StudentApp />} />

          {/* Games */}
          <Route path="/puzzle-hub" element={<PuzzleHub />} />
          <Route path="/snake-game" element={<SnakeGame />} />
          <Route path="/sliding-puzzle" element={<SlidingPuzzle />} />
          <Route path="/memory-match" element={<MemoryMatch />} />
          <Route path="/color-match" element={<ColorMatch />} />
          <Route path="/math-reflex" element={<MathReflex />} />
          <Route path="/seq-memory" element={<SequenceMemory />} />

          {/*Legal*/}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-conditions" element={<TermsConditions />} />

          {/* Fallback Route - MUST BE LAST */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </AnimatePresence>

      {shouldShowMainLayout && <Footer />}
    </div>
  );
};

function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 3500);

    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <MatrixLoader message="Zolang Nexora Softwares" />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
}

export default App;